"use client"

import { useMemo } from "react"
import type { VideoTutorialModalCopy } from "@/components/ui/video-tutorial-modal"
import { useAuth } from "@/lib/auth-context"
import { getAdminDictionary, toAdminLocale } from "./index"
import type { AdminDictionary, AdminLocale } from "./types"

type PathValue<T, K extends string> = K extends `${infer A}.${infer B}`
  ? A extends keyof T
    ? PathValue<T[A], B>
    : never
  : K extends keyof T
    ? T[K]
    : never

function resolvePath<T extends object>(obj: T, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, part) => {
    if (!acc || typeof acc !== "object") return undefined
    return (acc as Record<string, unknown>)[part]
  }, obj)
}

export function useAdminI18n() {
  const { user } = useAuth()
  const locale: AdminLocale = toAdminLocale(user?.profile?.preferredLanguage)
  const dictionary = useMemo(() => getAdminDictionary(locale), [locale])

  // Stable `t` so hooks that list it in dependency arrays (dashboard URL/tab sync) do not run every render.
  const t = useMemo(
    () =>
      <K extends string>(key: K): PathValue<AdminDictionary, K> => {
        const value = resolvePath(dictionary, key)
        return value as PathValue<AdminDictionary, K>
      },
    [dictionary]
  )

  return { locale, dictionary, t }
}

export function useVideoTutorialModalCopy(): VideoTutorialModalCopy {
  const { t } = useAdminI18n()
  return useMemo(
    () => ({
      triggerTooltip: t("videoTutorialModal.triggerTooltip"),
      openAriaLabel: (topic: string) => t("videoTutorialModal.openAriaLabel").split("{title}").join(topic),
      dialogTitle: (topic: string) => t("videoTutorialModal.dialogTitle").split("{title}").join(topic),
      iframeTitle: (topic: string) => t("videoTutorialModal.iframeTitle").split("{title}").join(topic),
    }),
    [t]
  )
}
